import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import get from 'lodash.get';
import { stopSubmit } from 'redux-form';

// Types
import type { Saga } from 'redux-saga';

// Constants
import {
    GROUP_CREATE_REQUESTED,
    GROUP_UPDATE_REQUESTED,
    GROUP_DELETE_REQUESTED
} from './constants';

// Actions
import { enqueueSnackbar } from '../notifications/actions';
import { selectCurrentOrganizationId } from '../currentOrganizationId/selectors';
import {
    groupCreated,
    failedCreateGroup,
    groupEdited,
    failedEditGroup,
    groupDeleted,
    failedDeleteGroup
} from './actions';
import { updateQueryString } from '../router/actions';

// Selectors
import { idTokenSelector } from '../auth/selectors';
import { selectCurrentSeasonId } from '../currentSeasonId/selectors';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* postGroup({ data }): Saga<void> {
    // Select Current Organization Id
    const organizationId = yield select(selectCurrentOrganizationId);

    const idToken = yield select(idTokenSelector);

    // Select Current Season Id
    const seasonId = yield select(selectCurrentSeasonId);

    const url = `${REACT_APP_API_URL}/v1/Groups`;

    const config = {
        method: 'post',
        url,
        headers: {
            Authorization: idToken
        },
        data: {
            ...data,
            organizationId,
            entitySeasonId: seasonId
        }
    };

    try {
        const response = yield call(axios, config);
        yield put(groupCreated(response.data));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Group created successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                groupModal: undefined,
                newGroup: response.data.id,
                defaultValues: undefined
            })
        );
    } catch (error) {
        yield put(failedCreateGroup(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('groupForm', { _error: message }));
    }
}

export function* watchCreateGroup(): Saga<void> {
    yield takeLatest(GROUP_CREATE_REQUESTED, postGroup);
}

export function* patchGroup({ id, data }): Saga<void> {
    const idToken = yield select(idTokenSelector);

    // Select Current Season Id
    const seasonId = yield select(selectCurrentSeasonId);

    const config = {
        method: 'patch',
        url: `${REACT_APP_API_URL}/v1/Groups/${id}`,
        headers: {
            Authorization: idToken
        },
        data: {
            ...data,
            entitySeasonId: seasonId
        }
    };

    try {
        const response = yield call(axios, config);
        yield put(groupEdited(id, response.data));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Group updated successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                groupModal: undefined,
                selectedGroup: undefined
            })
        );
    } catch (error) {
        yield put(failedEditGroup(error));

        const message = get(
            error,
            'response.data.message',
            'There was an error. Please try again later.'
        );

        // Redux form error
        yield put(stopSubmit('groupForm', { _error: message }));
    }
}

export function* watchEditGroup(): Saga<void> {
    yield takeLatest(GROUP_UPDATE_REQUESTED, patchGroup);
}

export function* deleteGroup({ id }): Saga<void> {
    const idToken = yield select(idTokenSelector);

    const config = {
        method: 'delete',
        url: `${REACT_APP_API_URL}/v1/Groups/${id}`,
        headers: {
            Authorization: idToken
        }
    };

    try {
        const response = yield call(axios, config);
        yield put(groupDeleted(id, response.data));

        // Enqueue Snackbar
        yield put(
            enqueueSnackbar({
                message: 'Group deleted successfully',
                options: { variant: 'success' }
            })
        );

        // Update Query String
        yield put(
            updateQueryString({
                groupDeleteModal: undefined,
                selectedGroup: undefined
            })
        );
    } catch (error) {
        yield put(failedDeleteGroup(error));
    }
}

export function* watchDeleteGroup(): Saga<void> {
    yield takeLatest(GROUP_DELETE_REQUESTED, deleteGroup);
}
